import { defineStore } from 'pinia';
import api from '@/api';

export const useAdminStore = defineStore('admin', {
    state: () => ({
        organizations: {},
        orgDropDown: [],
        orgLoading: {},
        users: {},
        usersLoading: {},
        departments: {},
        dptLoading: {},
        teams: {},
    }),
    getters: {},
    actions: {
        async fetchAllOrgs() {
            try {
                const res = await api.organization.fetch();

                const sortedData = res.data.sort((a, b) => a.name.localeCompare(b.name));
                for (let org of sortedData) {
                    this.orgDropDown.push({ value: org.id, text: org.name })
                    this.organizations[org.id] = org;
                }
            } catch (error) {
                console.error(error.message);
            }
        },
        async getOrgInfo(orgId) {
            try {
                // when a lot of components are trying to get the same org info, we could trigger a lot of identical requests.
                // instead we keep track of the ones currently loading, and wait for them to finish
                let delayMs = 0;
                while (this.orgLoading[orgId]) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    delayMs += 100;
                    if (delayMs > 3000) {
                        throw new Error("in admin->getOrgInfo: Timeout while waiting for org info " + orgId);
                    }
                }

                // here we know this specific org is not being fetched, so we check if it already cached
                if (this.organizations[orgId]) {
                    return this.organizations[orgId]
                }

                // here we tell other components that we are fetching this org, caching it, and returning it
                this.orgLoading[orgId] = true;
                const res = await api.organization.fetch(orgId);
                this.organizations[orgId] = res.data;
                this.orgLoading[orgId] = false;

                return res.data;

            } catch (error) {
                // releasing the lock on the org
                this.orgLoading[orgId] = false;
                console.error(error.message);
                return null;
            }
        },
        async getUserInfo(userId) {
            try {
                // when a lot of components are trying to get the same org info, we could trigger a lot of identical requests.
                // instead we keep track of the ones currently loading, and wait for them to finish
                let delayMs = 0;
                while (this.usersLoading[userId]) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    delayMs += 100;
                    if (delayMs > 3000) {
                        throw new Error("in admin->getUserINfo: Timeout while waiting for user " + userId);
                    }
                }

                // here we know this specific org is not being fetched, so we check if it already cached
                if (this.users[userId]) {
                    return this.users[userId]
                }

                // here we tell other components that we are fetching this org, caching it, and returning it
                this.usersLoading[userId] = true;
                const res = await api.user.fetchOne(userId);
                this.users[userId] = res.data;
                this.usersLoading[userId] = false;

                return res.data;

            } catch (error) {
                // releasing the lock on the org
                this.usersLoading[userId] = false;
                console.error(error.message);
                return null;
            }
        },
        async getDepartmentInfo(dptId) {
            try {
                // when a lot of components are trying to get the same org info, we could trigger a lot of identical requests.
                // instead we keep track of the ones currently loading, and wait for them to finish
                let delayMs = 0;
                while (this.dptLoading[dptId]) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    delayMs += 100;
                    if (delayMs > 3000) {
                        throw new Error("in admin->getDepartmentInfo: Timeout while waiting for dpt " + dptId);
                    }
                }

                // here we know this specific org is not being fetched, so we check if it already cached
                if (this.departments[dptId]) {
                    return this.departments[dptId]
                }

                // here we tell other components that we are fetching this org, caching it, and returning it
                this.dptLoading[dptId] = true;
                const res = await api.department.fetchOne(dptId);
                this.departments[dptId] = res.data;
                this.dptLoading[dptId] = false;

                return res.data;

            } catch (error) {
                // releasing the lock on the org
                this.dptLoading[dptId] = false;
                console.error(error.message);
                return null;
            }
        }
    },
});
